/**
 * Remainders (Trac #1054) — a read-only board for the previously-dark
 * `unbooked_remainder` queue: quotes where some items arrived and moved on
 * (tested, paid) while the rest never turned up and sit `pending`.
 *
 * Read-only by design. Receiving stays the single action surface: a late box
 * that turns up is ticked off there (which flips its Prism state and drops the
 * quote off this board on its own). Resolving a genuine no-show is deferred
 * (Phase 2); this board is the visibility surface and the measurement for it.
 */
import React, { useState, useEffect } from 'react'
import { api, formatCurrencyFromCents, formatDate } from '../../utils/api'
import DealDetailModal from './DealDetailModal'
import DealLink from '../shared/DealLink.jsx'

// Whole days since the partial receive, for the "waiting since" line. Null when
// unknown (never received, or an unparseable/future stamp) so the caller can
// fall back rather than render "NaN days".
function daysWaiting(iso) {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  return Math.floor(ms / 86400000)
}

function itemName(item) {
  return item.display_name
    || item.declared_name
    || [item.brand, item.model].filter(Boolean).join(' ')
    || 'Item'
}

function ItemList({ items, priced }) {
  if (items.length === 0) {
    return (
      <p className="text-muted" style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--font-size-sm)' }}>
        {priced ? 'None arrived yet' : 'Nothing pending'}
      </p>
    )
  }
  return (
    <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)', listStyle: 'disc' }}>
      {items.map(item => (
        <li key={item.quote_item_id} style={{ marginBottom: 'var(--space-1)', fontSize: 'var(--font-size-sm)' }}>
          {itemName(item)}{item.quantity > 1 ? ` ×${item.quantity}` : ''}
          {priced && item.buy_price_cents != null && (
            <span className="text-muted"> — {formatCurrencyFromCents(item.buy_price_cents)}</span>
          )}
          {priced && (
            <span
              className={`badge ${item.payment_id ? 'badge-success' : 'badge-warning'}`}
              style={{ marginLeft: 'var(--space-2)' }}
            >
              {item.payment_id ? 'Paid' : 'Unpaid'}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function RemainderCard({ deal, onOpen }) {
  const items = deal.items || []
  // `items` is live-only (rejected/returned are dead items, already excluded
  // upstream), so the split is simply pending vs everything else.
  const waiting = items.filter(i => i.state === 'pending')
  const arrived = items.filter(i => i.state && i.state !== 'pending')
  const days = daysWaiting(deal.first_received_at)

  return (
    <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
      <div className="card-header">
        <div>
          <button
            type="button"
            className="card-title"
            onClick={() => onOpen(deal)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-info)' }}
          >
            {deal.customer_name || 'Unknown seller'}
          </button>
          <span className="text-muted" style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
            <DealLink dealId={deal.id} reference={deal.reference_code} onOpen={() => onOpen(deal)} />
          </span>
        </div>
        <div className="text-muted" style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>
          {deal.first_received_at
            ? `Waiting since ${formatDate(deal.first_received_at)}${days != null ? ` · ${days} day${days === 1 ? '' : 's'}` : ''}`
            : 'Not yet received'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
        <section style={{ flex: '1 1 240px' }}>
          <div className="text-secondary" style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
            Still waiting ({waiting.length})
          </div>
          <ItemList items={waiting} priced={false} />
        </section>
        <section style={{ flex: '1 1 240px' }}>
          <div className="text-secondary" style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
            Arrived / paid ({arrived.length})
          </div>
          <ItemList items={arrived} priced={true} />
        </section>
      </div>
    </div>
  )
}

function Remainders() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDeal, setSelectedDeal] = useState(null)

  const loadData = async (spinner = true) => {
    if (spinner) setLoading(true)
    setError(null)
    try {
      const data = await api.get('/api/deals/remainders')
      setDeals(data.deals || [])
    } catch (e) {
      setError(e.message || 'Failed to load remainders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [])

  return (
    <div style={{ paddingTop: 'var(--space-4)' }}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 'var(--space-4)', marginBottom: 'var(--space-4)',
        }}
      >
        <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          Quotes where some items arrived but the rest never turned up. A late box clears itself once
          received in Receiving; the rest are no-shows to chase.
        </p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => loadData(false)} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading && <p className="text-muted">Loading…</p>}

      {error && (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
          <span className="badge badge-error">{error}</span>
        </div>
      )}

      {!loading && !error && deals.length === 0 && (
        <div className="card text-muted" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
          No remainders — nothing waiting.
        </div>
      )}

      {!loading && !error && deals.map(deal => (
        <RemainderCard key={deal.id ?? deal.quote_id} deal={deal} onOpen={setSelectedDeal} />
      ))}

      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onDealUpdated={() => { setSelectedDeal(null); loadData(false) }}
        />
      )}
    </div>
  )
}

export default Remainders
